import { useCallback, useMemo, useRef, useState } from 'react';
import { getBackendUrl } from '../lib/apiUrls';
import {
  buildNavigationTargetPath,
  normalizeNavigationTarget,
} from '../lib/appHelpers';
import { createWorkspaceClient } from '../lib/workspaceClient';

function normalizeCount(value) {
  return typeof value === 'number' && Number.isFinite(value) ? value : 0;
}

function normalizeEvidenceItem(item) {
  return {
    id: item?.id || '',
    sourceType: item?.sourceType || 'memory',
    title: item?.title || 'Untitled evidence',
    snippet: item?.snippet || '',
    score: typeof item?.score === 'number' ? item.score : 0,
    updatedAt: item?.updatedAt || null,
    navigationTarget: normalizeNavigationTarget(item?.navigationTarget),
  };
}

function normalizeContextSearchResult(payload, question) {
  return {
    question,
    answer: typeof payload?.answer === 'string' ? payload.answer : null,
    confidence:
      typeof payload?.confidence === 'number' && Number.isFinite(payload.confidence)
        ? payload.confidence
        : null,
    evidence: Array.isArray(payload?.evidence) ? payload.evidence.map(normalizeEvidenceItem) : [],
    searched: {
      memories: normalizeCount(payload?.searched?.memories),
      calendarEvents: normalizeCount(payload?.searched?.calendarEvents),
      tasks: normalizeCount(payload?.searched?.tasks),
      listItems: normalizeCount(payload?.searched?.listItems),
      queues: normalizeCount(payload?.searched?.queues),
    },
    notes: typeof payload?.notes === 'string' ? payload.notes : null,
  };
}

function buildSuccessMessage(result) {
  if (result.answer) {
    return 'Answer ready.';
  }

  if (result.evidence.length) {
    return 'No strong answer yet, but related evidence was found.';
  }

  return 'No matching context found.';
}

export function useContextSearch(currentUser) {
  const workspaceClient = useMemo(() => createWorkspaceClient(getBackendUrl()), []);
  const [question, setQuestion] = useState('');
  const [searchResult, setSearchResult] = useState(null);
  const [searchStatus, setSearchStatus] = useState({
    type: 'idle',
    message: 'Ask a question across memories, tasks, calendar, lists, and queue history.',
  });
  const [isSearching, setIsSearching] = useState(false);
  const latestRequestRef = useRef(0);

  const submitSearch = useCallback(
    async (rawQuestion = question) => {
      if (!currentUser?.userId) {
        setSearchStatus({
          type: 'error',
          message: 'Sign in to search workspace context.',
        });
        return false;
      }

      const trimmedQuestion = typeof rawQuestion === 'string' ? rawQuestion.trim() : '';
      if (!trimmedQuestion) {
        setSearchStatus({
          type: 'error',
          message: 'Enter a question before searching.',
        });
        return false;
      }

      const requestId = latestRequestRef.current + 1;
      latestRequestRef.current = requestId;
      setIsSearching(true);
      setSearchStatus({
        type: 'loading',
        message: 'Searching workspace context...',
      });

      try {
        const payload = await workspaceClient.searchContext(currentUser.userId, trimmedQuestion);
        if (latestRequestRef.current !== requestId) {
          return false;
        }

        const normalized = normalizeContextSearchResult(payload, trimmedQuestion);
        setSearchResult(normalized);
        setSearchStatus({
          type: 'success',
          message: buildSuccessMessage(normalized),
        });
        return true;
      } catch (error) {
        if (latestRequestRef.current !== requestId) {
          return false;
        }

        setSearchStatus({
          type: 'error',
          message: error?.error || error?.message || 'Could not search workspace context.',
        });
        return false;
      } finally {
        if (latestRequestRef.current === requestId) {
          setIsSearching(false);
        }
      }
    },
    [currentUser?.userId, question, workspaceClient]
  );

  return {
    question,
    searchResult,
    searchStatus,
    isSearching,
    setQuestion,
    submitSearch,
    buildNavigationPath: buildNavigationTargetPath,
  };
}

export default useContextSearch;
